import { Map } from "immutable";
import { useCallback, useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootReduxState } from "../../../../../utils/types";
import { startAuthProcessAction } from "../../redux/auth/actions";
import { addIntegrationAction, deleteIntegrationAction } from "../../redux/shopData/actions";
import { IntegrationType } from "../../types";
import { UsernamePasswordModal } from "./UsernamePasswordModal";

type UsernamePasswordSetupComponentOwnProps = {
  authKeyVisible: boolean;
  integrationType: IntegrationType;
  onFinish: () => void;
};

const mapStateToProps = (state: RootReduxState) => {
  const selectedShop = state.getIn([
    "customerBackendDashboard",
    "userData",
    "selectedShop"
  ]) as string;

  return {
    selectedShop,
    integrations: state.getIn([
      "customerBackendDashboard",
      "shops",
      selectedShop,
      "integrations",
      "data"
    ]) as Map<unknown, unknown> | undefined
  };
};
const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      addIntegrationAction,
      startAuthProcessAction,
      deleteIntegrationAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type UsernamePasswordSetupComponentProps = UsernamePasswordSetupComponentOwnProps &
  ConnectedProps<typeof connector>;

function UsernamePasswordSetupComponent(props: UsernamePasswordSetupComponentProps) {
  const {
    selectedShop,
    startAuthProcessAction,
    deleteIntegrationAction,
    addIntegrationAction,
    integrations = Map(),
    onFinish,
    integrationType,
    authKeyVisible
  } = props;

  const [addedId, setAddedId] = useState<number | undefined>(undefined);

  useEffect(() => {
    const newId = calculateNewId(integrationType);
    setAddedId(newId);
    addIntegrationAction(selectedShop, integrationType, { id: newId });
  }, []);

  const handleModalSuccess = useCallback(() => {
    setAddedId(undefined);
    onFinish();
  }, [onFinish]);

  const handleModalClose = useCallback(() => {
    if (!addedId) {
      return;
    }

    deleteIntegrationAction(selectedShop, integrationType, addedId);
    setAddedId(undefined);
    onFinish();
  }, [addedId, deleteIntegrationAction, selectedShop, integrationType, onFinish]);

  const handleModalSubmit = useCallback(
    (credentials: Map<unknown, unknown>) => {
      if (!addedId) {
        return;
      }

      startAuthProcessAction(integrationType, selectedShop, addedId, credentials.toJS());
    },
    [addedId, startAuthProcessAction, integrationType, selectedShop]
  );

  const calculateNewId = (type: string) => {
    if (integrations.size === 0) {
      return 0;
    }
    if (!integrations.get(type)) {
      return 0;
    }
    return (
      (integrations.get(type) as Map<unknown, unknown>[]).reduce(
        (p, c) => ((c.get("id") as number) > p ? (c.get("id") as number) : p),
        0
      ) + 1
    );
  };

  const integrationLineAdded = () =>
    integrations
      .getIn([integrationType])
      .find((settingLine: Map<unknown, unknown>) => settingLine.get("id") === addedId);

  const authReady = addedId !== undefined && integrationLineAdded();

  return (
    authReady && (
      <UsernamePasswordModal
        integrationType={integrationType}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        onSuccess={handleModalSuccess}
        selectedShop={selectedShop}
        id={addedId}
        authKeyVisible={authKeyVisible}
      />
    )
  );
}

export const UsernamePasswordSetup = connector(UsernamePasswordSetupComponent);
